import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { getTrainingModule } from 'http/trainingModules';
import {
  getTrainingPart,
  updateTrainingPart,
  getTrainingPartImpactedSessions,
  launchTrainingPart,
  cancelTrainingPart,
} from 'http/trainingParts';
import {
  getMoodleTrainingPartLanguageLinks,
  replaceMoodleTrainingPartLanguageLinks,
  getMoodleTrainingPartLanguageStatus,
  syncMoodleTrainingPartLanguages,
} from 'http/moodle';
import { routesAliases } from 'router/routesAliases';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROLES } from 'appConstants';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, openEntityLifecycleConfirm, resolveApiErrorMessage } from 'utils';
import TrainingPartForm from '../TrainingPartForm';
import TrainingPartFilesTable from '../TrainingPartFilesTable';
import { mapMoodleLinksToFormSelection } from '../helpers/buildMoodleLanguageLinksPayload';
import { hasTrainingPartScheduleImpactFieldsChanged } from '../helpers/hasTrainingPartScheduleImpactFieldsChanged';
import { useSessionsImpactConfirm } from '../helpers/sessionsImpactConfirm';
import LanguageStatus from 'pages/Templates/Edit/LanguageStatus';

const TRAINING_METHODS = ['theory', 'ojt', 'practical'];

const METHOD_TAB_INDEX = {
  theory: 1,
  practical: 2,
  ojt: 3,
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const EditTrainingPart = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = role === ROLES.HYPER;
  const isSuperAdmin = role === ROLES.SUPER;
  const { id: trainingModuleId, trainingPartId, trainingMethod } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [partData, setPartData] = useState(null);
  const [languageStatus, setLanguageStatus] = useState(null);
  const openSessionsImpactConfirm = useSessionsImpactConfirm();

  const backUrl = `${routesAliases.editTrainingModule}/${trainingModuleId}`;
  const backState = { activeTab: METHOD_TAB_INDEX[trainingMethod] ?? 0 };

  const isValidMethod = TRAINING_METHODS.includes(trainingMethod);

  const isSystemModule = Boolean(moduleData?.isFromSystem);
  const isPartReadOnly = isHyperAdmin ? false : isSuperAdmin ? isSystemModule : true;
  const canAddPartFiles = isHyperAdmin ? !isSystemModule : isSuperAdmin;
  const canDeletePartFiles = isHyperAdmin ? !isSystemModule : isSuperAdmin;
  const showLanguageSyncPanel =
    (isSuperAdmin || isHyperAdmin) &&
    isSystemModule &&
    trainingMethod === 'theory' &&
    Boolean(partData?.isFromSystem) &&
    Boolean(partData?.hasMoodleLink);

  const [fetchState, fetchData] = useAsyncFn(async () => {
    const [moduleResult, partResult] = await Promise.all([
      getTrainingModule(trainingModuleId),
      getTrainingPart(trainingPartId),
    ]);

    if (moduleResult?.error) {
      if (moduleResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { moduleResult, partResult };
    }

    if (partResult?.error) {
      if (partResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { moduleResult, partResult };
    }

    const moduleMatches = Number(partResult.trainingModuleId) === Number(trainingModuleId);
    const methodMatches = partResult.trainingMethod === trainingMethod;

    if (!moduleMatches || !methodMatches) {
      setNotFoundError(true);
      return { moduleResult, partResult };
    }

    let enrichedPart = partResult;
    if (trainingMethod === 'theory') {
      const moodleLinks = await getMoodleTrainingPartLanguageLinks(trainingPartId);
      if (!moodleLinks?.error && !moodleLinks?.statusCode) {
        enrichedPart = {
          ...partResult,
          moodleLanguageLinks: mapMoodleLinksToFormSelection(
            moodleLinks,
            partResult.code,
            partResult.version,
            partResult.revision,
          ),
        };
      }
      if ((isSuperAdmin || isHyperAdmin) && moduleResult.isFromSystem && partResult.isFromSystem) {
        const statusResult = await getMoodleTrainingPartLanguageStatus(trainingPartId);
        if (!statusResult?.error && !statusResult?.statusCode) {
          setLanguageStatus(statusResult);
        }
      }
    }

    setModuleData(moduleResult);
    setPartData(enrichedPart);
    setNotFoundError(false);
    return { moduleResult, partResult: enrichedPart };
  }, [isHyperAdmin, isSuperAdmin, trainingModuleId, trainingMethod, trainingPartId]);

  useEffect(() => {
    if (isValidMethod) {
      setNotFoundError(false);
      setModuleData(null);
      setPartData(null);
      fetchData();
    }
  }, [fetchData, isValidMethod, trainingModuleId, trainingMethod, trainingPartId]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: moduleData?.name || t('routes.editTrainingModule'),
        link: { pathname: backUrl, state: backState },
      },
      {
        title: t('trainingParts.editPageTitle'),
        isActive: true,
      },
    ],
    [backState, backUrl, moduleData?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const [submittingState, savePart] = useAsyncFn(
    async payload => {
      const { moodleLanguageLinks, ...partPayload } = payload;

      const persistPart = async () => {
        const result = await updateTrainingPart(trainingPartId, partPayload);
        if (result?.error || result?.statusCode) {
          return result;
        }

        if (trainingMethod === 'theory' && moodleLanguageLinks) {
          const linkResult = await replaceMoodleTrainingPartLanguageLinks(trainingPartId, moodleLanguageLinks);
          if (linkResult?.error || linkResult?.statusCode) {
            return linkResult;
          }
        }

        history.push(backUrl, backState);
        return result;
      };

      const isDraftPart = partData?.status === 'draft';
      const shouldPreviewImpact = !isDraftPart && hasTrainingPartScheduleImpactFieldsChanged(partData, partPayload);

      if (!shouldPreviewImpact) {
        return persistPart();
      }

      const impacted = await getTrainingPartImpactedSessions(trainingPartId);
      if (impacted?.error || impacted?.statusCode) {
        return impacted;
      }

      if (!impacted?.items?.length) {
        return persistPart();
      }

      return new Promise(resolve => {
        openSessionsImpactConfirm({
          sessions: impacted.items,
          onConfirm: async () => {
            const result = await persistPart();
            resolve(result);
          },
          onCancel: () => resolve(undefined),
        });
      });
    },
    [backState, backUrl, history, openSessionsImpactConfirm, partData, trainingMethod, trainingPartId],
  );

  const closeModal = useCallback(() => {
    dispatch(modalActions.closeModal());
  }, [dispatch]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [languageSyncState, syncLanguages] = useAsyncFn(
    async id => {
      const result = await syncMoodleTrainingPartLanguages(id);
      if (!result?.error && !(result?.statusCode && result.statusCode >= 400)) {
        await fetchData();
        return result;
      }
      showErrorModal(result?.message);
      return result;
    },
    [fetchData, showErrorModal],
  );

  const [launchState, launchPart] = useAsyncFn(async () => {
    const result = await launchTrainingPart(trainingPartId);
    if (!result?.error && !result?.statusCode) {
      await fetchData();
      return result;
    }
    showErrorModal(result.message);
    return result;
  }, [fetchData, showErrorModal, trainingPartId]);

  const [cancelState, cancelPart] = useAsyncFn(async () => {
    const result = await cancelTrainingPart(trainingPartId);
    if (!result?.error && !result?.statusCode) {
      await fetchData();
      return result;
    }
    showErrorModal(result.message);
    return result;
  }, [fetchData, showErrorModal, trainingPartId]);

  const confirmLaunchPart = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingParts',
      action: 'launch',
      onConfirm: launchPart,
    });
  }, [closeModal, dispatch, launchPart, t]);

  const confirmCancelPart = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingParts',
      action: 'cancel',
      onConfirm: cancelPart,
    });
  }, [cancelPart, closeModal, dispatch, t]);

  if (!isValidMethod || notFoundError) {
    return <NotFound />;
  }

  const additionSections = [];

  if (showLanguageSyncPanel) {
    additionSections.push({
      contentHeader: t('trainingParts.lmsLanguages.contentLanguages', 'Content languages'),
      component: <LanguageStatus status={languageStatus} />,
      actions: [
        {
          type: 'button',
          icon: 'sync',
          title: t('trainingParts.lmsLanguages.syncLmsLanguages', 'Sync LMS languages'),
          onClick: () => syncLanguages(trainingPartId),
          disabled: languageSyncState.loading,
        },
      ],
    });
  }

  if (partData?.id) {
    additionSections.push({
      component: (
        <TrainingPartFilesTable
          trainingPartId={partData.id}
          canAddFiles={canAddPartFiles}
          canDeleteFiles={canDeletePartFiles}
          canManageVisibility={canDeletePartFiles}
          isSystemModule={isSystemModule}
        />
      ),
    });
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || languageSyncState.loading || !moduleData || !partData}
      additionSections={additionSections}
    >
      {moduleData && partData ? (
        <TrainingPartForm
          mode="edit"
          layout="page"
          trainingMethod={trainingMethod}
          trainingModuleCode={moduleData.code}
          initialValues={partData}
          backUrl={backUrl}
          backState={backState}
          submitting={submittingState.loading || launchState.loading || cancelState.loading}
          onSubmitForm={savePart}
          onLaunch={isPartReadOnly ? null : confirmLaunchPart}
          onCancelEntity={isPartReadOnly ? null : confirmCancelPart}
          isReadOnly={isPartReadOnly}
        />
      ) : (
        <span />
      )}
    </PageLayout>
  );
};

EditTrainingPart.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default EditTrainingPart;
